/**
 * Contagens da estante por `count()` — contar sem ler os documentos.
 *
 * ## O bug que este módulo existe para fechar
 *
 * A meta anual era derivada da estante em memória (`countBooksFinishedInYear`
 * sobre o `UserBooksContext`). O contexto assina **uma janela** ordenada por
 * `updatedAt desc`, então a contagem só enxergava os livros carregados — e o
 * número mentia sem avisar assim que a estante passava da janela.
 *
 * Medido em 02/08 numa conta real: 110 livros, 8 concluídos em 2026, e a tela
 * dizia **3 de 12**. O gatilho foi o scanner em massa — 94 livros catalogados
 * em dois dias carimbaram `updatedAt` fresco e empurraram os livros realmente
 * lidos para o fim da ordenação, fora da janela. Não houve perda de dado: os
 * cinco que faltavam estavam nas posições 104-110 de 110.
 *
 * O erro de arquitetura não era o tamanho da janela — era **derivar contagem
 * de uma lista paginada**. Listar é paginável por natureza; contar não é.
 *
 * ## Por que `count()` e não um contador desnormalizado
 *
 * A alternativa era manter `shelfCounts`/`booksFinishedByYear` em `user_stats`
 * pelo trigger `onUserBookWritten`, que já roda. Custaria zero leitura (o doc
 * já é lido) e seria reativo — mas exige backfill, lógica de decremento no
 * trigger quando a data muda de ano, e passa a ter um estado que pode
 * dessincronizar do que ele descreve.
 *
 * `count()` não tem cópia para envelhecer: é a contagem real, toda vez. E é
 * barata — a cobrança é **1 document read por até 1000 entradas de índice**
 * varridas, então contar 45 livros custa 1 leitura, não 45.
 *
 * O preço é não ser reativa (agregação não suporta `onSnapshot`): quem
 * consome precisa refazer a busca quando a estante muda. Ver
 * `useBooksFinishedInYear`.
 *
 * ## Índice
 *
 * `user_books (userId ASC, status ASC, finishedAt DESC)` — já existe em
 * `firestore.indexes.json`. Query de range sem índice é erro de runtime, não
 * degradação silenciosa.
 */

import {
  and,
  collection,
  getCountFromServer,
  getDocs,
  or,
  query,
  type Query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../lib/firebase/config";
import type { BookStatus } from "./booksService";

/**
 * Fronteiras do ano no calendário **local**, como instantes.
 *
 * Espelha `yearOfLocal` (`utils/finishedDate.ts`), que é como todas as outras
 * superfícies derivam o ano de um livro. Tem que ser o mesmo frame, senão a
 * contagem do servidor e a divisória de ano da estante discordariam sobre o
 * livro terminado na virada — e ninguém saberia qual das duas está certa.
 *
 * ⚠️ Meia-noite local, e não `new Date(Date.UTC(...))`: a data declarada é
 * gravada ao **meio-dia local** (ver `isoToLocalDate`), então este intervalo
 * pega exatamente os livros cujo dia de calendário cai no ano pedido, com 12h
 * de folga de cada lado.
 */
export function localYearRange(year: number): { start: Date; end: Date } {
  return {
    start: new Date(year, 0, 1, 0, 0, 0, 0),
    end: new Date(year + 1, 0, 1, 0, 0, 0, 0),
  };
}

/**
 * Quantos livros o usuário concluiu no ano — contagem server-side, imune ao
 * tamanho da estante e ao que está carregado no client.
 *
 * Mesmo predicado do `countsForYear`: `status === "read"` **E** `finishedAt`
 * dentro do ano. Livro lido sem data declarada não conta — é estado normal e
 * de primeira classe (o onboarding e o scanner em massa gravam assim de
 * propósito), não um defeito a compensar.
 */
export async function countBooksFinishedInYearServer(
  userId: string,
  year: number,
): Promise<number> {
  const { start, end } = localYearRange(year);
  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    where("status", "==", "read"),
    where("finishedAt", ">=", Timestamp.fromDate(start)),
    where("finishedAt", "<", Timestamp.fromDate(end)),
  );
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

/** Os dois valores de posse. Ausência do campo = não tem. */
const OWNERSHIP_VALUES = ["physical", "digital"] as const;

/**
 * As quatro contagens da estante — as abas do seletor e os tiles do Perfil.
 *
 * 4 `count()` em paralelo ⇒ **4 leituras**, contra uma varredura da estante
 * inteira. E, ao contrário da derivação client-side, o número não depende de
 * quantos livros a janela carregou.
 *
 * ⚠️ **Os quatro NÃO somam o total da estante, e isso é o desenho** — mesma
 * regra do `computeShelfTileStats`. Três recortam o ciclo de leitura
 * (`status`, exclusivo); o quarto recorta a POSSE, que é eixo independente e
 * portanto um SUPERSET.
 *
 * ## Por que "Tenho" precisa de `or()`
 *
 * `isBookOwned` é `ownership ∈ {physical, digital}` **OU** o legado
 * `status === "owned"` (docs anteriores ao eixo de posse não têm o campo, e
 * Firestore **exclui** de um filtro todo doc que não tem o campo filtrado).
 * Medido em 02/08: `where("ownership","in",[...])` sozinho erra por 97, 18 e 1
 * livros nas três contas com legado — a ingênua devolvia **0 de 97** na maior
 * delas. O `or()` bate com `isBookOwned` nas três.
 *
 * Não exige índice composto novo: são filtros só de igualdade, e o Firestore
 * resolve por merge dos índices de campo único.
 */
export async function countShelfByStatus(userId: string): Promise<Record<BookStatus, number>> {
  const books = collection(db, "user_books");
  const owner = where("userId", "==", userId);

  const countOf = async (q: Query): Promise<number> => {
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  };
  const byStatus = (status: BookStatus) =>
    query(books, owner, where("status", "==", status));

  // ⚠️ `and(...)` explícito, e não `query(books, owner, or(...))`: um filtro
  // composto só entra na sobrecarga em que ele é o PRIMEIRO argumento depois da
  // coleção — misturá-lo com `where`s soltos não compila.
  const ownedQuery = query(
    books,
    and(
      owner,
      or(where("ownership", "in", [...OWNERSHIP_VALUES]), where("status", "==", "owned")),
    ),
  );

  const [reading, wantToRead, read, owned] = await Promise.all([
    countOf(byStatus("reading")),
    countOf(byStatus("want_to_read")),
    countOf(byStatus("read")),
    countOf(ownedQuery),
  ]);

  return { reading, want_to_read: wantToRead, read, owned };
}

/** Um livro terminado no ano, reduzido ao que a aba Ano das Estatísticas usa. */
export interface YearFinishedBook {
  id: string;
  title: string;
  coverUrl: string;
  finishedAtMs: number;
  /** `null` = sem data de início (antes de 15/09 quase ninguém tem). */
  startedAtMs: number | null;
  /** Físico com página conhecida; `0` quando não há (Kindle guarda %). */
  pages: number;
  /** Nota da estante; `null` = sem nota. */
  rating: number | null;
}

/**
 * Os livros terminados no ano — a aba Ano das Estatísticas v2 (26/09).
 *
 * MESMO predicado e MESMO intervalo da contagem da Meta
 * (`countBooksFinishedInYearServer`): os dois números da tela — "31 livros" e a
 * soma das barras por mês — saem da mesma pergunta e não podem discordar.
 * Custo: um documento por livro terminado no ano, só quando a aba é aberta.
 */
export async function fetchBooksFinishedInYear(
  userId: string,
  year: number,
): Promise<YearFinishedBook[]> {
  const { start, end } = localYearRange(year);
  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    where("status", "==", "read"),
    where("finishedAt", ">=", Timestamp.fromDate(start)),
    where("finishedAt", "<", Timestamp.fromDate(end)),
  );
  const snap = await getDocs(q);
  const ms = (v: unknown): number | null =>
    v && typeof (v as { toMillis?: unknown }).toMillis === "function"
      ? (v as { toMillis: () => number }).toMillis()
      : null;
  return snap.docs.map((d) => {
    const b = d.data() as Record<string, unknown>;
    const rating = typeof b.rating === "number" && b.rating > 0 ? b.rating : null;
    const pages =
      b.format === "physical" && typeof b.totalPages === "number" && b.totalPages > 0 ? b.totalPages : 0;
    return {
      id: d.id,
      title: typeof b.title === "string" ? b.title : "",
      coverUrl: typeof b.coverUrl === "string" ? b.coverUrl : "",
      finishedAtMs: ms(b.finishedAt) ?? 0,
      startedAtMs: ms(b.startedAt),
      pages,
      rating,
    };
  });
}
